import React, { useState, CSSProperties, FormEvent, useEffect, useRef } from "react";
import Select, { GroupBase } from "react-select";
import Form from 'react-bootstrap/Form';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { toLocalDateTimeString } from "../utils/helpers";
import TimeZoneDisplay from "./TimeZoneDisplay";
import { City, Country } from "../types/Lookups";
import { lookupCreateUrl } from "../utils/lookupUrl";
import { FieldHelp, LookupActions, lookupEntryNoun, lookupSelectProps, useCanAddLookup } from "./LookupMenu";

// Shared email-format check. Mirrors the regex used on the auth pages
// (login / forgotPassword) so every email field enforces the same rule:
// a real address with a TLD — "a@b" is rejected. EMAIL_PATTERN feeds the
// native <input pattern> so the browser blocks submit on form-based pages;
// EMAIL_REGEX drives the friendly inline message shown on blur.
const EMAIL_PATTERN = "([\\w.%+-]+)@([\\w-]+\\.)+([\\w]{2,})";
const EMAIL_REGEX = new RegExp(`^${EMAIL_PATTERN}$`, "i");

interface InputProps {
    type: "text" | "checkbox" | "textarea" | "select" | "date" | "datetime-local" | "email" | "tel" | "number" | "password";
    label?: string;
    id?: string;
    index?: number;
    value?: string | boolean | number | null;
    onChange: (name: string, value: string | boolean | object, index:number) => void;
    required?: boolean;
    options?: { value: string | number; label: string; disabled?: boolean; title?: string; style?: CSSProperties }[];
    rows?: number;
    style?: CSSProperties;
    className?: string;
    disabled?: boolean;
    /**
     * add icons class from https://icons.getbootstrap.com/
     * @example
     * "bi bi-0-circle"
     * **/
    icon?: string;
    allowFractions?: boolean;
    // Optional override for number input step attribute. If provided, takes precedence
    // over `allowFractions`. Pass "any" to allow arbitrary precision (e.g. lat/lng).
    step?: string | number;
    min?: number | string;
    max?: number | string;
    // For timezone display (can pass multiple cities/countries)
    cities?: (City | null)[];
    countries?: (Country | null)[];
    // Hover help — when provided, renders an info icon in the top-right
    // corner of the input wrapper that surfaces this text on hover/focus.
    tooltip?: string;
    /**
     * Admin-page URL for lookup-driven dropdowns. When provided alongside
     * a tooltip, the tooltip is extended with a "Manage: <route> →" line
     * and a small open-in-new-tab icon appears next to the info icon.
     * Click opens the admin page in a new browser tab so the user can
     * add / edit lookup entries without losing context on the current
     * editor.
     */
    adminPageUrl?: string;
    /** Optional explicit CREATE-page URL for the open-lookup icon. When unset,
     *  the create route is derived from adminPageUrl (plural list -> singular
     *  create). Pass this for irregular/nested create routes. */
    createPageUrl?: string;
    /**
     * Friendly name of the lookup admin page, used in the tooltip
     * "Manage:" line and the open-link icon's title attribute. Falls
     * back to a humanised version of the route if omitted.
     */
    adminPageLabel?: string;
    /**
     * Callback fired when the user clicks the small refresh icon next
     * to a lookup picker. Parents wire this to the same fetch they ran
     * on initial mount so a new lookup entry (added via the admin page)
     * appears in the picker immediately. When omitted, the refresh icon
     * is not rendered.
     */
    onRefresh?: () => void;
    /**
     * Externally controlled invalid state. When true, the field renders
     * the Bootstrap is-invalid red border and the `errorMessage`
     * underneath (via Form.Control.Feedback). Use this when the parent
     * owns validation (e.g. form-wide errors object) instead of letting
     * Input's built-in required-check drive the state.
     */
    isInvalid?: boolean;
    /**
     * Message rendered under the field when `isInvalid` is true. Falls
     * back to the internal required-field error message when omitted.
     */
    errorMessage?: string;
    /**
     * The value differs from the last saved one — the field gets the gold
     * "unsaved" ring so a direct-edit page shows what Save will send.
     */
    isChanged?: boolean;
    /**
     * Native attributes for the text-like fields (text, email, tel, password):
     * the form-field name and the autofill / keyboard hints sign-in pages need
     * (autoComplete "username" / "current-password" / "one-time-code"…).
     * Without it the field keeps autoComplete="off".
     */
    nativeProps?: Pick<
        React.InputHTMLAttributes<HTMLInputElement>,
        "name" | "autoComplete" | "autoFocus" | "inputMode" | "maxLength" | "autoCapitalize" | "autoCorrect" | "spellCheck" | "enterKeyHint"
    >;
    /** Trim surrounding spaces on blur (default). Pass false for passwords, where a space is part of the value. */
    trimOnBlur?: boolean;
}

/** Types whose label sits on the field's top border once it has a value. */
const NOTCHED_LABEL_TYPES = new Set(["text", "email", "tel", "password", "number", "textarea", "select", "date", "datetime-local"]);
/** Types whose empty state shows the label as its placeholder (an empty datetime shows the browser's date mask). */
const PLACEHOLDER_LABEL_TYPES = new Set(["text", "email", "tel", "password", "number", "textarea", "select", "date"]);

const Input: React.FC<InputProps> = ({
    type,
    label,
    id='',
    value,
    onChange,
    required = false,
    options = [],
    rows = 3,
    style,
    className,
    disabled,
    index,
    icon,
    allowFractions = false,
    step,
    min,
    max,
    cities,
    countries,
    tooltip,
    adminPageUrl,
    createPageUrl,
    adminPageLabel,
    onRefresh,
    isInvalid: isInvalidProp,
    errorMessage,
    isChanged = false,
    nativeProps,
    trimOnBlur = true,
}) => {
    const [error, setError] = useState<string | undefined>(undefined);
    const [showTimezoneModal, setShowTimezoneModal] = useState(false);

    const [initialValue, setInitialValue] = useState(value);
    useEffect(() => {
        setInitialValue(value); // Set initial value on mount or when value prop changes
    }, [value]);

        const handleInputChange = (
            e: FormEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
            name: string,
            index: number
        ) => {
            let inputValue: string | boolean = (e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;
            if (type === "checkbox") {
                inputValue = (e.target as HTMLInputElement).checked;
            }
            if (type === "number") {
                // Surface out-of-range as a non-blocking message but NEVER drop the
                // keystroke — onChange below always fires so the field stays responsive.
                // Native min/max attributes + blur/submit validation enforce the bounds.
                const numericValue = parseFloat(inputValue as string);
                if (min !== undefined && typeof min === "number" && numericValue < min) {
                    setError(`Value must be greater than or equal to ${min}`);
                } else if (max !== undefined && typeof max === "number" && numericValue > max) {
                    setError(`Value must be less than or equal to ${max}`);
                } else {
                    setError(undefined); // Clear error if value is valid
                }
            }
            // Convert datetime-local value to ISO string (UTC) for consistent backend storage
            if (type === "datetime-local" && inputValue) {
                const localDate = new Date(inputValue as string);
                if (!isNaN(localDate.getTime())) {
                    inputValue = localDate.toISOString();
                }
            }
            if (inputValue !== value) {
                onChange(name, inputValue, index);
            }
            
            if (required) {
            setError(
                inputValue.toString().trim() === "" ? `${label} is required` : undefined
            );
            }
        };
    const externallyInvalid = !!isInvalidProp;
    const isInvalid = !!error || externallyInvalid;
    const controlStyle: CSSProperties = isInvalid ? { borderColor: "var(--field-invalid)", ...style } : style || {};


        // to handle placeholder and value in date picker
        const [inputType, setInputType] = useState<'text' | 'date'>('text');
        const handleDateFocus = () => {
            setInputType('date');
        };
        const handleDateBlur = () => {
            setInputType('text');
        };

        const handleBlur = (
          e: React.FocusEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
          >
        ) => {
          if (trimOnBlur && e.target.value) {
            const value =e.target.value;
            if (initialValue && (value !== (initialValue as string).trim())) {
                const trimmedValue = e.target.value.trim();
              onChange(id || "", trimmedValue, index || 0);
            }
          }
          // Email format is validated on blur (not per-keystroke) so the
          // field doesn't flash "invalid" while the user is still typing.
          // Required fields must be both non-empty AND a valid address;
          // optional fields are only format-checked when non-empty. Native
          // required + pattern attributes still enforce this on submit.
          if (type === "email") {
            const trimmed = e.target.value.trim();
            if (trimmed === "") {
              setError(required ? `${label} is required` : undefined);
            } else if (!EMAIL_REGEX.test(trimmed)) {
              setError("Please enter a valid email address");
            } else {
              setError(undefined);
            }
          }
        };
    // Push the absolute trailing icons (tooltip / refresh / open-link)
    // further left when the field has its own right-edge chrome so they
    // never sit on top of it:
    //  - `select`     → native dropdown caret.
    //  - `datetime-local` → native calendar/clock picker indicator AND
    //                       the appended hourglass timezone icon.
    //  - `date`       → native calendar picker indicator.
    // Without this the info-circle lands directly over the native
    // picker glyph and the two visibly overlap.
    const baseRight =
        type === "select" ? 28
        : type === "datetime-local" ? 48
        : type === "date" ? 28
        : 8;
    const tooltipIconRight = `${baseRight}px`;
    const refreshIconRight = `${baseRight + 18}px`;
    const openLinkIconRight = `${baseRight + (onRefresh ? 36 : 18)}px`;
    // Lookup selects (option A, 2026-10-05): the shared searchable select, with
    // Add new / Reload in its open list and the help in the notched label, so
    // nothing sits inside the field.
    const isLookupSelect = type === "select" && !!(adminPageUrl || createPageUrl || onRefresh);
    // Help lives with the label: in the notch once the field has a value, and
    // right after the label's words while it's empty — in a lookup's
    // placeholder, or for a plain field on a see-through copy of its native
    // placeholder (which can't hold an icon). Only an empty datetime, which
    // shows the browser's date mask instead of the label, keeps it at the end.
    const hasValue = value !== null && value !== undefined && String(value) !== "";
    const ghostHelp = !!tooltip && !!label && !hasValue && !isLookupSelect && PLACEHOLDER_LABEL_TYPES.has(type);
    // A checkbox carries its help right after its own label text, never drawn over it.
    const helpInLabel = !!tooltip && !!label && (type === "checkbox" || isLookupSelect || ghostHelp || (hasValue && NOTCHED_LABEL_TYPES.has(type)));
    // Room for the icons still drawn inside the field, so a long value never runs under them.
    const iconCount = isLookupSelect ? 0 : (tooltip && !helpInLabel ? 1 : 0) + (onRefresh ? 1 : 0) + (adminPageUrl ? 1 : 0);
    const iconPadding: CSSProperties = iconCount > 0 && type !== "checkbox" ? { paddingRight: `${baseRight + iconCount * 18 + 6}px` } : {};

    // Friendly admin page label — falls back to a humanised version of
    // the path (e.g. /equipment-types → Equipment Types).
    const humanisedAdminLabel = (() => {
        if (adminPageLabel) return adminPageLabel;
        if (!adminPageUrl) return null;
        const slug = adminPageUrl.replace(/^\/+/, "").split("/")[0] ?? "";
        return slug
            .split("-")
            .filter(Boolean)
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
    })();

    // Tooltip body — when this field is a lookup-driven picker, append
    // a "Manage:" line so the operator knows where to add new entries.
    const tooltipBody = tooltip && adminPageUrl
        ? `${tooltip}\n\nManage: ${humanisedAdminLabel ?? "Admin page"} →`
        : tooltip;

    const tooltipIcon = tooltipBody && !helpInLabel ? (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`${id}-tooltip`} style={{ whiteSpace: "pre-line" }}>{tooltipBody}</Tooltip>}
        >
            <i
                className="bi bi-info-circle"
                tabIndex={0}
                role="img"
                aria-label={`${label ?? "field"} help`}
                style={{
                    position: "absolute",
                    top: type === "checkbox" ? "2px" : "6px",
                    right: tooltipIconRight,
                    cursor: "help",
                    color: "var(--btn-icon)",
                    // Kept BELOW open dropdown menus (Bootstrap
                    // .dropdown-menu = 1000, react-select menu = 1).
                    // The icon needs to sit above the field's static
                    // chrome but never above an open list, otherwise
                    // it visually punches through option rows.
                    zIndex: 1,
                    fontSize: "var(--fs-md)",
                }}
            />
        </OverlayTrigger>
    ) : null;

    // Refresh icon — sits between the info icon and the open-link icon
    // (so the row reads, right-to-left: tooltip / refresh / open-link)
    // and re-fetches the lookup options on click.
    const refreshIcon = onRefresh && !isLookupSelect ? (
        <i
            className="bi bi-arrow-clockwise"
            role="button"
            tabIndex={0}
            title="Reload list"
            aria-label="Reload list"
            onClick={(e) => { e.stopPropagation(); onRefresh(); }}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRefresh();
                }
            }}
            style={{
                position: "absolute",
                top: type === "checkbox" ? "2px" : "6px",
                right: refreshIconRight,
                cursor: "pointer",
                zIndex: 1,
                fontSize: "var(--fs-md)",
            }}
        />
    ) : null;

    // Open-link icon — opens the lookup admin page in a new tab so the
    // user can add a new entry without losing context here. The new
    // entry surfaces on the next refresh (manual or window-focus).
    const openLinkIcon = adminPageUrl && !isLookupSelect ? (
        <i
            className="bi bi-box-arrow-up-right"
            role="button"
            tabIndex={0}
            title={`Add a new ${humanisedAdminLabel ?? "entry"} in a new tab`}
            aria-label={`Add a new ${humanisedAdminLabel ?? "entry"} in a new tab`}
            onClick={(e) => {
                e.stopPropagation();
                window.open(createPageUrl ?? lookupCreateUrl(adminPageUrl), "_blank");
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    window.open(createPageUrl ?? lookupCreateUrl(adminPageUrl), "_blank");
                }
            }}
            style={{
                position: "absolute",
                top: type === "checkbox" ? "2px" : "6px",
                right: openLinkIconRight,
                cursor: "pointer",
                zIndex: 1,
                fontSize: "var(--fs-md)",
            }}
        />
    ) : null;

    // Placeholders carry the label only while the field is empty, so a filled
    // field keeps a small label on its top border to say what the value is.
    const labelHelp = helpInLabel && tooltip ? <FieldHelp id={id} label={label} text={isLookupSelect ? tooltip : tooltipBody ?? tooltip} /> : null;
    // An empty date-time field shows the browser mask (mm/dd/yyyy) instead of its
    // placeholder, so it always carries the notched label.
    const notchedLabel = label && (hasValue || type === "datetime-local") && NOTCHED_LABEL_TYPES.has(type) ? (
        <label className="crx-field-label" htmlFor={id || undefined}>
            {label}
            {required && <span className="crx-field-label-req" aria-hidden="true"> *</span>}
            {labelHelp}
        </label>
    ) : null;
    const ghostLabel = ghostHelp ? (
        <span className={`crx-field-ghost${type === "textarea" ? " crx-field-ghost--top" : ""}`}>
            <span className="crx-field-ghost-text" aria-hidden="true">{label}</span>
            {labelHelp}
        </span>
    ) : null;
    // A page may resize its field (the payment amount is 56px tall with its
    // currency inside), so the ghost copies the control's real box — measured
    // when it first lays out, e.g. once a hidden tab opens, and on resize.
    const fieldRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!ghostHelp || typeof ResizeObserver === "undefined") return;
        const ghost = fieldRef.current?.querySelector<HTMLElement>(":scope > .crx-field-ghost");
        const control = fieldRef.current?.querySelector<HTMLElement>(":scope > input, :scope > select, :scope > textarea");
        if (!ghost || !control) return;
        const place = () => {
            if (!control.offsetHeight) return;
            const style = window.getComputedStyle(control);
            const px = (v: string) => parseFloat(v) || 0;
            ghost.style.top = `${control.offsetTop}px`;
            ghost.style.left = `${control.offsetLeft}px`;
            ghost.style.paddingLeft = `${px(style.borderLeftWidth) + px(style.paddingLeft)}px`;
            ghost.style.maxWidth = `${Math.max(0, control.offsetWidth - 30)}px`;
            if (type === "textarea") ghost.style.paddingTop = `${px(style.borderTopWidth) + px(style.paddingTop)}px`;
            else ghost.style.lineHeight = `${control.offsetHeight}px`;
        };
        const observer = new ResizeObserver(place);
        observer.observe(control);
        return () => observer.disconnect();
    }, [ghostHelp, type]);

    // "Add new" only for users who may create in the lookup (Reload stays).
    const canAddLookup = useCanAddLookup(adminPageUrl);
    const lookupActions: LookupActions | undefined = isLookupSelect
        ? {
            noun: lookupEntryNoun(label ?? humanisedAdminLabel, adminPageLabel),
            adminPageUrl: canAddLookup ? adminPageUrl : undefined,
            createPageUrl: canAddLookup ? createPageUrl : undefined,
            onRefresh,
        }
        : undefined;
    const lookupOptions = isLookupSelect
        ? options.map((option) => ({ value: String(option.value), label: option.label, isDisabled: !!option.disabled, title: option.title, style: option.style }))
        : [];
    type LookupOption = (typeof lookupOptions)[number];
    const lookupValue = isLookupSelect ? lookupOptions.find((option) => option.value === String(value ?? "")) ?? null : null;
    const handleLookupChange = (option: LookupOption | null) => {
        const next = option ? option.value : "";
        if (next !== String(value ?? "")) onChange(id, next, index || 0);
        if (required) setError(next === "" ? `${label} is required` : undefined);
    };

    const wrapperClass = [
        "mb-3",
        "crx-field",
        isChanged ? "crx-field--changed" : "",
        disabled ? "crx-field--disabled" : "",
        isInvalid ? "crx-field--invalid" : "",
    ].filter(Boolean).join(" ");

    return (
    <div ref={fieldRef} className={wrapperClass} style={{ position: "relative" }}>
        {notchedLabel}
        {ghostLabel}
        {tooltipIcon}
        {refreshIcon}
        {openLinkIcon}
        {/* {error && <span className="errorClass">{error}</span>} */}
                {/* <FloatingLabel
                    // controlId={id}
                    className={`mb-3`}
                    label={renderLabel}
                > */}
        {type === "checkbox" ? (
        <div className="d-flex align-items-center">
            <Form.Check
            type="checkbox"
            disabled={disabled}
            key={index}
            id={id}
            checked={value as boolean}
            onChange={(e) => handleInputChange(e, id , index || 0)}
            style={controlStyle}
            label={<span className="ms-2 label-small-font">{label}{labelHelp}</span>}
            title={label}
            isInvalid={isInvalid}
            />
        </div>
        ) : type === "textarea" ? (
        <Form.Control
            as="textarea"
            className={className}
            disabled={disabled}
            id={id}
            key={index}
            value={value as string}
            onChange={(e) => handleInputChange(e, id , index || 0)}
            required={required}
            rows={rows}
            style={{ ...iconPadding, ...controlStyle }}
            placeholder={label}
            title={label}
            autoComplete="off"
            onBlur={handleBlur}
            isInvalid={isInvalid}
            {...nativeProps}
                    />
        ) : isLookupSelect ? (
        <Select<LookupOption, false>
            inputId={id || undefined}
            instanceId={id || undefined}
            className={`custom-select${isInvalid ? " custom-select--invalid" : ""}`}
            classNamePrefix="custom-select"
            aria-label={label}
            aria-invalid={isInvalid || undefined}
            placeholder={<span>{label}{required ? " *" : ""}{labelHelp}</span>}
            options={lookupOptions}
            value={lookupValue}
            onChange={handleLookupChange}
            formatOptionLabel={(option) => (option.title || option.style
                ? <span title={option.title} style={option.style}>{option.label}</span>
                : option.label)}
            isDisabled={disabled}
            isClearable={!required}
            required={required}
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPlacement="auto"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            {...lookupSelectProps<LookupOption, false, GroupBase<LookupOption>>(lookupActions)}
        />
        ) : type === "select" ? (
        <Form.Select aria-label={label}
        size="sm"
                className={`form-select ${value === "" || value === undefined || value === null ? 'select-placeholder' : ''}`}
                id={id}
                key={index}
                value={value === null || value === undefined ? "" : String(value)}
                onChange={(e) => handleInputChange(e, id , index || 0)}
                style={{ ...iconPadding, ...controlStyle }}
                disabled={disabled}
                title={label}
                isInvalid={isInvalid}
                required={required}
            >
            <option value="">{label} </option>
                {options.map((option) => (
                <option
                key={option.value}
                value={option.value}
                className="select-options"
                disabled={option.disabled}
                title={option.title}
            >
                {option.label}
            </option>
                ))}

        </Form.Select>
        ) : type === "date" ? (
            <input
            // Empty: plain text so the label shows as the placeholder. Filled: a
            // real date field, so the value reads in the user's date format.
            type={hasValue ? "date" : inputType}
            onFocus={handleDateFocus}
            onBlur={handleDateBlur}
            className={className}
            disabled={disabled}
            id={id}
            key={index}
            value={value as string}
            onChange={(e) => handleInputChange(e, id , index || 0)}
            min={min ? min.toString() : undefined}
            max={max ? max.toString() : undefined}
            required={required}
            style={{ ...iconPadding, ...controlStyle }}
            placeholder={label}
            title={label}
            autoComplete="off"
        />
        ) : type === "datetime-local" ? (
            <>
            <div className="input-group">
                <input
                type="datetime-local"
                className={className}
                disabled={disabled}
                id={id}
                key={index}
                value={value && !isNaN(new Date(value as string).getTime()) ? toLocalDateTimeString(value as string) : ""}
                onChange={(e) => handleInputChange(e, id , index || 0)}
                min={min ? toLocalDateTimeString(min) : undefined}
                max={max ? toLocalDateTimeString(max) : undefined}
                required={required}
                style={{ ...iconPadding, ...controlStyle }}
                placeholder={label}
                title={label}
                autoComplete="off"
            />
            <i className="bi bi-hourglass-split ms-2" hidden={disabled || !value}
                onClick={() => setShowTimezoneModal(true)}></i>
            </div>
            <TimeZoneDisplay
                dateTime={value as string}
                show={showTimezoneModal}
                onHide={() => setShowTimezoneModal(false)}
                cities={cities}
                countries={countries}
                label={label}
            />
            </>
        ): type === "number" ? (
            <Form.Control
                size="sm"
                type="number"
                step={step ?? (allowFractions ? "0.001" : "1")}
                min={min}
                max={max}
                className={className}
                disabled={disabled}
                id={id}
                key={index}
                value={value as string}
                onChange={(e) => handleInputChange(e, id , index || 0)}
                required={required}
                style={{ ...iconPadding, ...controlStyle }}
                placeholder={label}
                title={label}
                autoComplete="off"
                isInvalid={isInvalid}
                //onBlur={handleBlur}
            />
        ) : (
            <>
                <Form.Control
                size="sm"
                    type={type}
                    pattern={type === "email" ? EMAIL_PATTERN : undefined}
                    className={className}
                    disabled={disabled}
                    id={id}
                    key={index}
                    value={value as string}
                    onInput={(e) => handleInputChange(e, id , index || 0)}
                    required={required}
                    style={{ ...iconPadding, ...controlStyle }}
                    placeholder={label}
                    title={label}
                    autoComplete="off"
                    {...nativeProps}
                    onBlur={handleBlur}
                    isInvalid={isInvalid}
                    />
                    
                </>
        )}
        {error ? (
            <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
        ) : externallyInvalid && errorMessage ? (
            <div className="invalid-feedback d-block">{errorMessage}</div>
        ) : null}
        {/* </FloatingLabel> */}
    </div>
    );
};

export default Input;
